import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * useUnreadMessagesCount — SINGLETON shared across all consumers
 * --------------------------------------------------------------
 * Plusieurs composants (Sidebar, AdminSidebar, GlobalChatFab) consomment ce hook
 * simultanément. Avant : chaque consommateur lançait son propre fetch +
 * setInterval → N appels redondants à /conversations. Après : un seul fetch
 * + un seul interval (60s) partagés via un store module-level + subscribers.
 *
 *  - count : total (rétro-compat)
 *  - assignedCount : staff uniquement, conv où user est l'agent assigné
 *  - othersCount : staff uniquement, conv non assignées à ce user
 */

// Module-level singleton store
let _state = { count: 0, assignedCount: 0, othersCount: 0 };
let _subscribers = new Set();
let _intervalId = null;
let _currentUserKey = null; // "userId|role" — permet de détecter un switch de user
let _inFlight = null;

const _notify = () => {
  _subscribers.forEach(fn => {
    try { fn(_state); } catch { /* ignore */ }
  });
};

const _fetchOnce = async (userId, userRole) => {
  if (!userId) return;
  // Dédoublonne les appels concurrents
  if (_inFlight) return _inFlight;
  _inFlight = (async () => {
    try {
      const res = await axios.get(`${API}/conversations`);
      const list = res.data || [];
      const isClient = ['client', 'expediteur'].includes(userRole);
      if (isClient) {
        const total = list.reduce((a, c) => a + (c.unreadCountClient || 0), 0);
        _state = { count: total, assignedCount: 0, othersCount: 0 };
      } else {
        let assigned = 0;
        let others = 0;
        for (const conv of list) {
          const u = conv.unreadCount || 0;
          if (u <= 0) continue;
          if (conv.assignedAgentId === userId) assigned += u;
          else others += u;
        }
        _state = { count: assigned + others, assignedCount: assigned, othersCount: others };
      }
      _notify();
    } catch (e) {
      // silencieux (évite spam console)
    } finally {
      _inFlight = null;
    }
  })();
  return _inFlight;
};

const _startPolling = (userId, userRole) => {
  if (_intervalId) return;
  _intervalId = setInterval(() => _fetchOnce(userId, userRole), 60000);
};

const _stopPolling = () => {
  if (_intervalId) {
    clearInterval(_intervalId);
    _intervalId = null;
  }
};

export const useUnreadMessagesCount = () => {
  const { user } = useAuth();
  const [local, setLocal] = useState(_state);

  const refresh = useCallback(() => {
    if (user?.id) _fetchOnce(user.id, user.role);
  }, [user?.id, user?.role]);

  useEffect(() => {
    if (!user?.id) return;
    const userKey = `${user.id}|${user.role}`;

    // Si le user change, reset le cache (évite fuite de data inter-utilisateurs)
    if (_currentUserKey && _currentUserKey !== userKey) {
      _state = { count: 0, assignedCount: 0, othersCount: 0 };
      _stopPolling();
    }
    _currentUserKey = userKey;

    _subscribers.add(setLocal);
    // Si 1er subscriber : fetch initial + start polling
    if (_subscribers.size === 1) {
      _fetchOnce(user.id, user.role);
      _startPolling(user.id, user.role);
    } else {
      // Nouveau subscriber : on lui donne la dernière valeur connue
      setLocal(_state);
    }

    const handler = () => _fetchOnce(user.id, user.role);
    window.addEventListener('baobab:conversation-updated', handler);

    return () => {
      _subscribers.delete(setLocal);
      window.removeEventListener('baobab:conversation-updated', handler);
      if (_subscribers.size === 0) _stopPolling();
    };
  }, [user?.id, user?.role]);

  return { ...local, refresh };
};

export default useUnreadMessagesCount;
